import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Building2, Copy, Loader2, Users, FileText, Check } from 'lucide-react';
import { toast } from 'sonner';
import { seminarApi } from '../../lib/api';
import { InHouseInvoiceList } from './InHouseInvoiceList';

const eur = (n) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

/**
 * Dialog zur Konfiguration eines In-House-Kurses. Zwei Zustände:
 * (1) Kurs noch nicht als In-House markiert → Formular für Firma + Preistyp
 * (2) Bereits markiert → Signup-Link kopieren, Teilnehmer-Zahl, Sammelrechnung
 */
export function InHouseDialog({ open, onOpenChange, seminar, onUpdated }) {
    const [companyName, setCompanyName] = useState('');
    const [contactPerson, setContactPerson] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [priceType, setPriceType] = useState('flat');
    const [flatPrice, setFlatPrice] = useState('');
    const [perParticipantPrice, setPerParticipantPrice] = useState('');
    const [maxP, setMaxP] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [linkInfo, setLinkInfo] = useState(null);
    const [copied, setCopied] = useState(false);
    const [generating, setGenerating] = useState(false);

    const [invoices, setInvoices] = useState([]);

    const isMarked = !!seminar?.is_in_house;

    const loadInvoices = () => seminarApi.listInHouseInvoices(seminar.id)
        .then(r => setInvoices(r.data))
        .catch(() => setInvoices([]));

    useEffect(() => {
        if (!open || !seminar) return;
        if (isMarked) {
            seminarApi.getInHouseSignupUrl(seminar.id)
                .then(r => setLinkInfo(r.data))
                .catch(e => toast.error(e.response?.data?.detail || 'Signup-Link konnte nicht geladen werden'));
            loadInvoices();
        } else {
            // Vorbelegen
            setCompanyName(seminar.in_house_company?.name || '');
            setContactPerson(seminar.in_house_company?.contact_person || '');
            setEmail(seminar.in_house_company?.email || '');
            setPhone(seminar.in_house_company?.phone || '');
            setPriceType(seminar.in_house_price_type || 'flat');
            setFlatPrice(seminar.in_house_flat_price != null ? String(seminar.in_house_flat_price) : '');
            setPerParticipantPrice(seminar.in_house_per_participant_price != null ? String(seminar.in_house_per_participant_price) : '');
            setMaxP(seminar.in_house_max_participants != null ? String(seminar.in_house_max_participants) : String(seminar.max_participants || ''));
        }
    }, [open, seminar, isMarked]);

    const submitMark = async () => {
        if (!companyName.trim() || companyName.trim().length < 2) {
            toast.error('Bitte Firmenname angeben');
            return;
        }
        const flatNum = parseFloat(flatPrice);
        const perNum = parseFloat(perParticipantPrice);
        if (priceType === 'flat' && (!isFinite(flatNum) || flatNum < 0)) {
            toast.error('Bitte einen gültigen Pauschalpreis angeben');
            return;
        }
        if (priceType === 'per_participant' && (!isFinite(perNum) || perNum < 0)) {
            toast.error('Bitte einen gültigen Preis pro Teilnehmer angeben');
            return;
        }
        setSubmitting(true);
        try {
            await seminarApi.markInHouse(seminar.id, {
                company: {
                    name: companyName.trim(),
                    contact_person: contactPerson.trim() || null,
                    email: email.trim() || null,
                    phone: phone.trim() || null,
                },
                price_type: priceType,
                flat_price: priceType === 'flat' ? flatNum : null,
                per_participant_price: priceType === 'per_participant' ? perNum : null,
                max_participants: maxP ? parseInt(maxP, 10) : null,
            });
            toast.success('Kurs als In-House-Fortbildung markiert. Signup-Link wurde erstellt.');
            onUpdated && onUpdated();
            // Reload URL-Info (im nächsten Tick nach Reload)
            setTimeout(async () => {
                try {
                    const r = await seminarApi.getInHouseSignupUrl(seminar.id);
                    setLinkInfo(r.data);
                } catch { /* egal */ }
            }, 200);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Markierung fehlgeschlagen');
        } finally {
            setSubmitting(false);
        }
    };

    const copyLink = async () => {
        if (!linkInfo?.path) return;
        const full = `${window.location.origin}${linkInfo.path}`;
        try {
            await navigator.clipboard.writeText(full);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
            toast.success('Link kopiert');
        } catch {
            toast.error('Konnte Link nicht kopieren – bitte manuell markieren');
        }
    };

    const generateInvoice = async () => {
        setGenerating(true);
        try {
            const r = await seminarApi.generateInHouseInvoice(seminar.id);
            toast.success(`Sammelrechnung ${r.data.invoice_number} über ${eur(r.data.amount)} erstellt (${r.data.participant_count} TN)`, { description: r.data.dropbox_path ? `In Dropbox abgelegt: ${r.data.dropbox_path}` : 'Dropbox-Ablage nicht möglich (Dropbox nicht verbunden)' });
            loadInvoices();
            onUpdated && onUpdated();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Rechnung konnte nicht erstellt werden');
        } finally {
            setGenerating(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="in-house-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-primary" />
                        {isMarked ? 'Firmenfortbildung – Verwaltung' : 'Als Firmenfortbildung (In-House) markieren'}
                    </DialogTitle>
                    <DialogDescription>
                        {isMarked
                            ? 'Der Kurs ist als In-House-Fortbildung markiert. Kopieren Sie den Link und senden Sie ihn an die Firma.'
                            : 'In-House-Kurse sind nicht öffentlich buchbar. Die Firma erhält einen Link und meldet ihre Mitarbeiter selbst an.'}
                    </DialogDescription>
                </DialogHeader>

                {!isMarked ? (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="ih-company">Firma *</Label>
                            <Input id="ih-company" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Firma GmbH" data-testid="ih-company" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-2">
                                <Label htmlFor="ih-contact">Ansprechpartner</Label>
                                <Input id="ih-contact" value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} data-testid="ih-contact" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="ih-email">E-Mail</Label>
                                <Input id="ih-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="ih-email" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="ih-phone">Telefon</Label>
                            <Input id="ih-phone" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="ih-phone" />
                        </div>

                        <div className="space-y-2 border-t pt-3">
                            <Label className="text-sm font-semibold">Preisgestaltung</Label>
                            <RadioGroup value={priceType} onValueChange={setPriceType} className="space-y-1" data-testid="ih-price-type">
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <RadioGroupItem value="flat" id="pt-flat" />
                                    Pauschalpreis (unabhängig von TN-Zahl)
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <RadioGroupItem value="per_participant" id="pt-per" />
                                    Preis pro Teilnehmer
                                </label>
                            </RadioGroup>
                            {priceType === 'flat' ? (
                                <div className="space-y-2">
                                    <Label htmlFor="ih-flat">Pauschalpreis (netto, EUR) *</Label>
                                    <Input id="ih-flat" type="number" step="0.01" min="0" value={flatPrice} onChange={(e) => setFlatPrice(e.target.value)} placeholder="1500.00" data-testid="ih-flat-price" />
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <Label htmlFor="ih-per">Preis pro Teilnehmer (netto, EUR) *</Label>
                                    <Input id="ih-per" type="number" step="0.01" min="0" value={perParticipantPrice} onChange={(e) => setPerParticipantPrice(e.target.value)} placeholder="100.00" data-testid="ih-per-price" />
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="ih-max">Max. Teilnehmerzahl</Label>
                                <Input id="ih-max" type="number" min="1" value={maxP} onChange={(e) => setMaxP(e.target.value)} data-testid="ih-max-participants" />
                            </div>
                        </div>
                        <DialogFooter className="gap-2 sm:gap-0">
                            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>Abbrechen</Button>
                            <Button onClick={submitMark} disabled={submitting} data-testid="ih-submit">
                                {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Building2 className="h-4 w-4 mr-1.5" />}
                                Als In-House markieren
                            </Button>
                        </DialogFooter>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="border rounded-lg p-3 bg-muted/30">
                            <div className="text-xs text-muted-foreground mb-1">Firma</div>
                            <div className="font-semibold">{seminar.in_house_company?.name}</div>
                            {seminar.in_house_company?.contact_person && (
                                <div className="text-xs text-muted-foreground mt-1">Ansprechpartner: {seminar.in_house_company.contact_person}</div>
                            )}
                            {seminar.in_house_company?.email && (
                                <div className="text-xs text-muted-foreground">E-Mail: {seminar.in_house_company.email}</div>
                            )}
                        </div>

                        <div>
                            <Label className="text-sm font-semibold">Anmeldelink für die Firma</Label>
                            {linkInfo?.path ? (
                                <div className="mt-1 flex items-center gap-1">
                                    <Input
                                        readOnly
                                        value={`${window.location.origin}${linkInfo.path}`}
                                        className="text-xs"
                                        data-testid="ih-signup-link"
                                    />
                                    <Button variant="outline" size="sm" onClick={copyLink} data-testid="ih-copy-link" title="Link kopieren">
                                        {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                                    </Button>
                                </div>
                            ) : (
                                <p className="text-xs text-muted-foreground mt-1">Kein Link vorhanden</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">Diesen Link an die Firma senden — dort kann sie Rechnungsanschrift und Mitarbeiter eintragen.</p>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <div className="border rounded-lg p-3">
                                <div className="text-xs text-muted-foreground flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Angemeldete TN</div>
                                <div className="text-xl font-bold" data-testid="ih-participants-count">{linkInfo?.participants_count ?? 0}</div>
                            </div>
                            <div className="border rounded-lg p-3">
                                <div className="text-xs text-muted-foreground">Preistyp</div>
                                <div className="text-sm font-semibold">
                                    {seminar.in_house_price_type === 'flat'
                                        ? `Pauschal ${eur(seminar.in_house_flat_price)}`
                                        : `${eur(seminar.in_house_per_participant_price)} / TN`}
                                </div>
                            </div>
                        </div>

                        {linkInfo?.invoice_address ? (
                            <div className="border rounded-lg p-3 bg-emerald-50 border-emerald-200 text-xs">
                                <div className="font-semibold mb-0.5 text-emerald-900">Rechnungsanschrift erhalten</div>
                                <div className="text-emerald-900">{linkInfo.invoice_address.name}</div>
                                <div className="text-emerald-900">{linkInfo.invoice_address.street}</div>
                                <div className="text-emerald-900">{linkInfo.invoice_address.zip} {linkInfo.invoice_address.city}</div>
                                {linkInfo.invoice_address.vat_id && <div className="text-emerald-900">USt-ID: {linkInfo.invoice_address.vat_id}</div>}
                            </div>
                        ) : (
                            <div className="border rounded-lg p-3 bg-amber-50 border-amber-200 text-xs text-amber-900" data-testid="ih-no-address">
                                Rechnungsanschrift der Firma steht noch aus – die Sammelrechnung kann erst erstellt werden, wenn die Firma sie eingetragen hat.
                            </div>
                        )}

                        <Button
                            onClick={generateInvoice}
                            disabled={generating || !linkInfo?.invoice_address || invoices.length > 0}
                            className="w-full"
                            data-testid="ih-generate-invoice"
                        >
                            {generating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />}
                            Sammelrechnung erstellen
                        </Button>
                        {invoices.length > 0 && (
                            <p className="text-xs text-muted-foreground -mt-2" data-testid="ih-invoice-exists-hint">
                                Für diesen Kurs wurde bereits eine Sammelrechnung erstellt. Eine neue Rechnung ist erst möglich, wenn die bestehende (unbezahlte) Rechnung gelöscht wurde.
                            </p>
                        )}

                        <InHouseInvoiceList invoices={invoices} onChanged={loadInvoices} />

                        <DialogFooter>
                            <Button variant="ghost" onClick={() => onOpenChange(false)}>Schließen</Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default InHouseDialog;
